import { Injectable } from '@angular/core';
import { Observable, from, of, throwError, firstValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { openDB, IDBPDatabase } from 'idb'; 

@Injectable({
  providedIn: 'root'
})
export class S3MultipartUploaderService {
  private readonly CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
  private dbPromise: Promise<IDBPDatabase<any>> | null = null;
  private readonly DB_NAME = 'video-upload-db';
  private readonly STORE_NAME = 'upload-chunks';

  constructor(private http: HttpClient) {
    this.initDB();
  }

  private async initDB() {
    try {
      const { openDB } = await import('idb');
      this.dbPromise = openDB(this.DB_NAME, 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('upload-chunks')) {
            const store = db.createObjectStore('upload-chunks', { keyPath: 'id', autoIncrement: true });
            store.createIndex('sessionId', 'sessionId');
          }
        },
      });
    } catch (e) {
      console.warn('idb not available', e);
    }
  }

  async cacheChunk(sessionId: string, chunk: Blob, partNumber: number): Promise<number> {
    if (!this.dbPromise) return -1;
    const db = await this.dbPromise;
    const id = await db.put(this.STORE_NAME, {
      sessionId,
      partNumber,
      chunk,
      timestamp: Date.now()
    });
    return id as number;
  }

  async getCachedChunks(sessionId: string): Promise<any[]> {
    if (!this.dbPromise) return [];
    const db = await this.dbPromise;
    return await db.getAllFromIndex(this.STORE_NAME, 'sessionId', sessionId);
  }

  async removeCachedChunk(id: number): Promise<void> {
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    await db.delete(this.STORE_NAME, id);
  }

  async clearSessionCache(sessionId: string): Promise<void> {
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    const index = db.transaction(this.STORE_NAME, 'readwrite').store.index('sessionId');
    let cursor = await index.openCursor(sessionId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
  }

  async uploadPartToS3(presignedUrl: string, blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          if (etag) {
            resolve(etag);
          } else {
            reject(new Error('No ETag received from S3'));
          }
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      };
      
      xhr.onerror = () => {
        reject(new Error('Network error during S3 upload'));
      };
      
      xhr.send(blob);
    });
  }

}
